import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, FileText } from "lucide-react";
import { QUOTE_STATUS, QUOTE_PRODUCTS, eur } from "@/lib/crm";

const EMPTY = {
  category: "Efficienza", product: "", amount: "", discount: "", status: "Bozza",
  validity_days: "", sent_date: "", follow_up_date: "", notes: "", client_notes: "",
};

export default function QuoteDialog({ open, onOpenChange, customerId, quote, prefill, onSaved }) {
  const editing = !!quote;
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
    if (quote) {
      setF({
        category: quote.category || "Efficienza", product: quote.product || "", amount: quote.amount ?? "",
        discount: quote.discount ?? "", status: quote.status || "Bozza", validity_days: quote.validity_days ?? "",
        sent_date: quote.sent_date || "", follow_up_date: quote.follow_up_date || "",
        notes: quote.notes || "", client_notes: quote.client_notes || "",
      });
    } else {
      setF({ ...EMPTY, ...(prefill || {}) });
    }
  }, [open, quote, prefill]);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const finalAmount = f.amount === "" ? null : Number(f.amount) - (f.discount === "" ? 0 : Number(f.discount));

  const save = async () => {
    if (f.amount !== "" && f.discount !== "" && Number(f.discount) > Number(f.amount)) {
      setError("Lo sconto non può superare l'importo"); return;
    }
    setError(""); setSaving(true);
    try {
      const payload = {
        customer_id: customerId, category: f.category, product: f.product || null,
        amount: f.amount === "" ? null : Number(f.amount),
        discount: f.discount === "" ? null : Number(f.discount),
        validity_days: f.validity_days === "" ? null : Number(f.validity_days),
        sent_date: f.sent_date || null, follow_up_date: f.follow_up_date || null,
        status: f.status, notes: f.notes || null, client_notes: f.client_notes || null,
        opportunity_id: prefill?.opportunity_id || quote?.opportunity_id || null,
        supply_id: prefill?.supply_id || quote?.supply_id || null,
        supply_contract_id: prefill?.supply_contract_id || quote?.supply_contract_id || null,
      };
      const { data } = editing ? await api.put(`/quotes/${quote.id}`, payload) : await api.post(`/quotes`, payload);
      toast.success(editing ? "Preventivo aggiornato" : `Preventivo ${data.quote_number} creato`);
      onSaved?.(data);
      onOpenChange(false);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto bg-white sm:max-w-lg" data-testid="quote-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display"><FileText className="h-5 w-5 text-brand" /> {editing ? `Preventivo ${quote.quote_number}` : "Nuovo preventivo"}</DialogTitle>
          <DialogDescription>Numero assegnato automaticamente. Il PDF è scaricabile dopo il salvataggio.</DialogDescription>
        </DialogHeader>
        {error && <div className="rounded-lg border border-danger/25 bg-danger/10 px-3 py-2 text-sm text-danger" data-testid="quote-error">{error}</div>}

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Categoria</Label>
              <Select value={f.category} onValueChange={(v) => set("category", v)} disabled={!!prefill?.category}>
                <SelectTrigger data-testid="quote-category"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white"><SelectItem value="Commodity">Commodity (Luce/Gas)</SelectItem><SelectItem value="Efficienza">Efficienza</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Prodotto/Soluzione</Label>
              <Select value={f.product || "none"} onValueChange={(v) => set("product", v === "none" ? "" : v)}>
                <SelectTrigger data-testid="quote-product"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent className="bg-white"><SelectItem value="none">—</SelectItem>{QUOTE_PRODUCTS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5"><Label>Importo (€)</Label><Input type="number" step="0.01" value={f.amount} onChange={(e) => set("amount", e.target.value)} data-testid="quote-amount" /></div>
            <div className="space-y-1.5"><Label>Sconto (€)</Label><Input type="number" step="0.01" value={f.discount} onChange={(e) => set("discount", e.target.value)} data-testid="quote-discount" /></div>
            <div className="space-y-1.5"><Label>Totale</Label>
              <div className="flex h-10 items-center rounded-md border border-border bg-slate-50 px-3 text-sm font-semibold text-brand" data-testid="quote-final">{finalAmount == null ? "—" : eur(finalAmount)}</div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Stato</Label>
              <Select value={f.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="quote-status"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{QUOTE_STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Validità (gg)</Label><Input type="number" value={f.validity_days} onChange={(e) => set("validity_days", e.target.value)} data-testid="quote-validity" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Data invio</Label><Input type="date" value={f.sent_date} onChange={(e) => set("sent_date", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Follow-up</Label><Input type="date" value={f.follow_up_date} onChange={(e) => set("follow_up_date", e.target.value)} data-testid="quote-followup" /></div>
          </div>
          <div className="space-y-1.5"><Label>Note interne</Label><Textarea value={f.notes} onChange={(e) => set("notes", e.target.value)} rows={2} data-testid="quote-notes" /></div>
          <div className="space-y-1.5"><Label>Note per il cliente (in PDF)</Label><Textarea value={f.client_notes} onChange={(e) => set("client_notes", e.target.value)} rows={2} data-testid="quote-client-notes" /></div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="quote-cancel">Annulla</Button>
          <Button onClick={save} disabled={saving} data-testid="quote-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
